import omitBy from 'lodash/omitBy';
import { get, post, put, del, patch } from 'utils/apiClient';
import { getReEnterPasswordToken } from '../utils/authCookie';
import NativeTitleStatus from 'constant/NativeTitle';

export async function getUser(id) {
  return get(`users/${id}`);
}
export async function updateUser(id, data) {
  return put(`users/${id}`, {
    body: data,
  });
}

export async function pauseUnpauseUser(id, data) {
  return put(`users/${id}/pause-unpause`, {
    body: data,
  });
}

export async function addUserToVerifierRole(id, data) {
  return put(`users/${id}/to-verifier-role`, {
    body: data,
  });
}

export async function sendRequestToUserToAddVerifierRole(id, data) {
  return put(`users/${id}/request-to-verifier-role`, {
    body: data,
  });
}

export async function resetVerifierPin(id) {
  return put(`users/${id}/reset-verifier-pin`);
}

export async function getAll(params = null, { signal } = {}) {
  const filteredParams = omitBy(
    params,
    (v) => v === undefined || v === null || v === '',
  );

  try {
    // if your `get` already returns { status, data }, this is fine
    const res = await get('users', { params: filteredParams, signal });
    // normalize: some wrappers return axios response, some return {status, data}
    if (res && res.status !== undefined && res.data !== undefined) {
      return { status: res.status, data: res.data };
    }
    return res;
  } catch (err) {
    // If axios-like error, return status/data from err.response to the caller instead of throwing
    if (err && err.response) {
      return { status: err.response.status, data: err.response.data };
    }
    // If it was an abort/cancel, re-throw so caller can detect
    throw err;
  }
}

export async function getUsersWithTags(params = null, { signal } = {}) {
  const filteredParams = omitBy(
    params,
    (v) => v === undefined || v === null || v === '',
  );
  return get('users/with-tags', { params: filteredParams, signal });
}

export async function listAllUser(params) {
  return get('users/list-all', { params });
}

export async function associateUser(params) {
  return post('enterprise/associate-user', { body: params });
}

export async function addUser(params) {
  return post('users', { body: params });
}

export async function getUserDetails() {
  return get('users/details');
}

export async function getUserInfo() {
  return get('user');
}

export async function getUserFinancialDetails() {
  return get('users/details/financial');
}

export async function getUserDetailsById(id) {
  return get(`users/${id}/details`);
}

export async function getUserFinancialDetailsById(id) {
  return get(`users/${id}/details/financial`);
}

export async function updateUserDetails(data) {
  return put(
    Boolean(data.onBoarded) ? 'users/details-onboarding' : 'users/details',
    { body: data },
  );
}

export async function updateUserDetailsById(id, data) {
  return put(`users/${id}/details`, { body: data });
}

export async function getUserDetailsExcel(userId) {
  return get(`users/${userId}/download-details`, { responseType: 'blob' });
}
export async function getUserResume(userId, config = {}) {
  return post(`resume/${userId}`, { responseType: 'blob', ...config });
}

export async function getUserCredentials(userId) {
  return post(`users/${userId}/generate_credentials`);
}

export async function saveIdentificationImage(data) {
  return post('users/identification_image', {
    body: data,
  });
}

export async function deleteUser(userId) {
  return del(`users/${userId}`);
}

export async function unlinkUserEnterprise(userId, enterpriseId) {
  return post(`users/${userId}/unlink-user-enterprise`, {
    body: { enterpriseId },
  });
}

export async function getMyLinkedEnterprises() {
  return get(`user/my-linked-enterprises`);
}

export async function restoreUser(userId) {
  return post(`users/${userId}/restore`);
}

export async function getGenderStats({ type }) {
  return get(`user/gender-stats`, { params: { type } });
}

export async function getUserCard(userId) {
  return get(`users/${userId}/current_card`);
}

export async function getVerifierRoleRequest(requestId) {
  return get(`users/${requestId}/verifier-role-request`);
}

export async function verifyRequest(requestId, body) {
  return put(`users/${requestId}/to-verifier-role`, { body });
}

export async function verifyUserByPhone(body) {
  return post(`users/verify-user-by-phone`, { body });
}

export async function getPhoneVerificationQuestions(userId) {
  return get(`users/${userId}/phone-verification-questions`);
}

export async function getAgeStats({ type }) {
  return get(`user/age-stats`, { params: { type } });
}

export async function getOriginalityStats({ type }) {
  return get(`user/originality-stats`, { params: { type } });
}

export async function getJobSectorStats({ type }) {
  return get(`user/job-sector-stats`, { params: { type } });
}

export async function getTilesStats() {
  return get(`user/tile-stats`);
}

export async function getTribalNationStats() {
  return get(`user/tribal-nation-stats`);
}

export async function getSkinGroups() {
  return get(`users/details/skinGroup`);
}

export async function getOldestAncestors() {
  return get(`users/details/oldestAncestor`);
}

export async function getEstateBelongings() {
  return get(`users/details/estateBelongings`);
}

export async function getTribalNations() {
  return get(`users/details/tribalNation`);
}

export async function getUserLogs(userId, params = null) {
  const filteredParams = omitBy(
    params,
    (v) => v === undefined || v === null || v === '',
  );
  return get(`users/${userId}/logs`, { params: filteredParams });
}

export async function getUserStatus(userId) {
  return get(`users/${userId}/status`);
}

export async function getUserPermissions(userId, data) {
  return post(`users/${userId}/payment-permissions`, { body: data });
}

export async function updateUserLoginMethods(data) {
  return post(`user/preffered-login-methods`, {
    body: data,
  });
}

export async function verifyWorker(id, data) {
  return patch(`experiences/${id}`, { body: data });
}

export async function getVerificationQuestions() {
  return get('verification/questions');
}

export async function addVerificationQuestionsAnswer(id, body) {
  return post(`verification/answers/${id}/submit`, {
    body: {
      memberOfPBC: body.memberOfPBC,
      registeredIndigenousAssociation: body.registeredIndigenousAssociation,
      apicalAncestorName: body.apicalAncestorName,
      tribeMemberName: body.tribeMemberName,
    },
  });
}

export async function checkUserTraditionalStatus(id) {
  return get(`traditional-user-status/${id}`);
}

export async function getNewUserList(
  isImported,
  options = {},
  { signal } = {},
) {
  const params = omitBy(
    {
      page: options.page,
      limit: options.limit,
      search: options.search,
    },
    (v) => v === undefined || v === null || v === '',
  );

  const queryString = new URLSearchParams(params).toString();
  // The backend route is defined as /new-user-list/:isImported
  // isImported is boolean true/false
  const endpoint = `new-user-list/${isImported}?${queryString}`;

  return get(endpoint, { signal });
}

export async function getNewUserDetail(id) {
  return get(`new-user-detail/${id}`);
}

export async function verifyImage(id) {
  return put(`verify-image/${id}`);
}

export async function verifyImageNew(userId, id) {
  return put(`verify-image-new/${userId}/${id}`);
}

export async function intitateIDV(userId) {
  return post(`idkit/initiate-verification`, {
    body: { name: '', email: '', userId: userId },
  });
}

export async function fetchNativeTitleDetail(id) {
  return get(`verification/native-title-detail/${id}`);
}

export async function updateNativeTitleDetail(id) {
  return put(`verification/update-native-title/${id}`, {
    body: {
      status: NativeTitleStatus.ACCEPTED,
    },
  });
}

export async function verifyRequestedUser(id) {
  return put(`verify-requested-user/${id}`);
}

export async function updateShareInformationConsent(id, data) {
  return put(`user/update-user-consent/${id}`, {
    body: data,
  });
}

export async function getShareInformationConsent() {
  return get(`user/associated-enterprises`);
}

export async function getShareInformationConsentByEnterprise(userId) {
  return get(`user/get-user-consent/${userId}`);
}

export async function getUserReCaptureFacialVerification(userId) {
  return get(`user/get-user-recapture-face/${userId}`);
}
